// /verify-email — landing page for the URL in the verification email.
//
// Reads ?token=... from the query string, POSTs it to identity-service,
// and renders one of: verifying / verified / expired / mismatch / error.
// The user got here by clicking a link in their inbox, so we want a
// clean confirmation UI and a clear CTA back into the dashboard.

import { useEffect, useState } from "react";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { Logo } from "../components/Logo";
import { useI18n } from "../lib/i18n";
import { ApiError, auth } from "../lib/api";

type State =
  | { kind: "verifying" }
  | { kind: "verified"; email: string }
  | { kind: "expired" }
  | { kind: "mismatch" }
  | { kind: "missing_token" }
  | { kind: "error"; msg: string };

function VerifyEmail() {
  const { t } = useI18n();
  const search = useSearch({ from: "/verify-email" }) as { token?: string };
  const [s, setS] = useState<State>({ kind: "verifying" });

  useEffect(() => {
    const token = search.token?.trim();
    if (!token) {
      setS({ kind: "missing_token" });
      return;
    }
    let cancelled = false;
    auth.verifyEmail(token)
      .then((r) => {
        if (!cancelled) setS({ kind: "verified", email: r.email });
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 410) {
          // Server returns 410 Gone for "expired/used" OR "email
          // changed". We collapse them in the UI — both mean
          // "request a new link" from the user's perspective.
          const msg = (e.body as { error?: string })?.error ?? "";
          setS({ kind: msg.includes("email address") ? "mismatch" : "expired" });
        } else {
          setS({ kind: "error", msg: e instanceof Error ? e.message : String(e) });
        }
      });
    return () => { cancelled = true; };
  }, [search.token]);

  return (
    <div className="min-h-full flex items-center justify-center px-4">
      <Card className="w-full max-w-md p-8 flex flex-col gap-5">
        <Link to="/login" className="ring-nexus rounded self-start"><Logo size="sm" /></Link>
        {s.kind === "verifying" && (
          <>
            <h1 className="font-display text-2xl font-semibold">{t("verifyEmail.verifying")}</h1>
            <p className="text-secondary text-sm">{t("verifyEmail.verifyingHint")}</p>
          </>
        )}
        {s.kind === "verified" && (
          <>
            <h1 className="font-display text-2xl font-semibold text-success">✓ {t("verifyEmail.verified")}</h1>
            <p className="text-secondary text-sm">{t("verifyEmail.verifiedHint").replace("{email}", s.email)}</p>
            <Link to="/login"><Button className="w-full">{t("verifyEmail.continueToLogin")}</Button></Link>
          </>
        )}
        {(s.kind === "expired" || s.kind === "mismatch" || s.kind === "missing_token") && (
          <>
            <h1 className="font-display text-2xl font-semibold text-error">{t("verifyEmail.cannotVerify")}</h1>
            <p className="text-secondary text-sm">
              {s.kind === "missing_token" ? t("verifyEmail.missingToken")
                : s.kind === "mismatch"     ? t("verifyEmail.mismatch")
                                            : t("verifyEmail.expired")}
            </p>
            <Link to="/login"><Button className="w-full" variant="secondary">{t("verifyEmail.backToLogin")}</Button></Link>
          </>
        )}
        {s.kind === "error" && (
          <>
            <h1 className="font-display text-2xl font-semibold text-error">{t("common.errorGeneric")}</h1>
            <p className="text-secondary text-sm font-mono">{s.msg}</p>
            <Link to="/login"><Button className="w-full" variant="secondary">{t("verifyEmail.backToLogin")}</Button></Link>
          </>
        )}
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/verify-email")({
  component: VerifyEmail,
  validateSearch: (s: Record<string, unknown>) => ({
    token: typeof s.token === "string" ? s.token : undefined,
  }),
});
